import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { downloadDnaPdf, wellnessReportUrl } from "../../utils/constants";
import useApis from "../../services/useAPI";
import ReportDownloadSection from "./ReportDownloadSection";
import { format_date, track } from "../../utils/utils";
import ReportAccordion from "../../stories/Accordion/Accordion";
import { Spinner } from "react-bootstrap";
import DnaPartialReportCard from "../../stories/cards/DnaPartialReportCard";

// Function to fix a known typo in exclusions name
const fix_exclusions_name = name =>
  name === "Red Meat excluding Port" ? "Red Meat excluding Pork" : name;

// Helper function for keyboard interactions
const createKeyHandler = (tag, applyFilter) => (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    applyFilter("goal", tag);
  }
};

/**
 * The WellnessReportPage component renders the wellness report details,
 * including health areas, goals, exclusions, and download options.
 */

const WellnessReportPage = () => {
  const [reports, setReports] = useState([]);
  const { callApi, resultGet, isLoading } = useApis();
  const navigate = useNavigate();
  const location = useLocation();

  const fullName = localStorage.getItem("username");
  const userName = fullName ? fullName.split(" ")[0] : "";
  const { id } = useParams();
  const [isGlutenPartial, setIsGlutenPartial] = useState(false); 
  const [partialGluten, setPartialGluten] = useState([]);

  useEffect(() => {
    if (id) {
      callApi(null, wellnessReportUrl(id), "GET");
    }
  }, [id]);

  useEffect(() => {
    if (resultGet) {
      setReports([resultGet]);
      if(resultGet?.type === "wellness-intolerance"){
        const isPartial= resultGet?.health_areas?.find(area => area.tag === "gluten-intolerance");
        setIsGlutenPartial(isPartial?.partial_data);
        if(isPartial?.partial_data === true){
          setPartialGluten(isPartial);
        }
      }
    }
  }, [resultGet]);

  const params = new URLSearchParams(location.search);
  const goal_filter = params.get("goal");

  const applyFilter = (filter, new_value) => {
    const old_value = params.get(filter);
    if (new_value === old_value) {
      params.delete(filter);
    } else {
      track(`report-filter-${filter}`, new_value);
      params.set(filter, new_value);
    }
    navigate(`?${params.toString()}`);
  };

  const getAccordionItems = (report) => {
    return report.health_areas.map(area => ({
      name: area.name,
      tag: area.tag,
      personalised_description: area.personalised_description,
      description: area.description,
      effect: area.effect,
      value: area.value,
      lead: area.lead,
      advice: area.advice,
      advisements: report.advice,
      snps: report.snps.filter(item => area.snps.includes(item.snp)),
      further_research_required: report.further_research_required,
      missing: area.missing
    }));
  };

  // Memoizes accordion items to prevent re-calculating on every render
  const memoizedReports = useMemo(
    () =>
      reports?.map(report => ({
        ...report,
        accordionItems: getAccordionItems(report)
      })),
    [reports]
  );

  const renderGoalButton = (g) => {
    const handleClick = () => applyFilter("goal", g.tag);
    const handleKeyPress = createKeyHandler(g.tag, applyFilter);

    return (
      <button
        key={g.tag}
        className={`small-pill clickable ${goal_filter === g.tag ? " filled" : ""}`}
        onClick={handleClick}
        onKeyPress={handleKeyPress}
        tabIndex={0}
      >
        {g.name}
      </button>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="text-center">
          <Spinner animation="border" className="custom-spinner my-custom-spinner" />
        </div>
      );
    }

    if (memoizedReports.length === 0) {
      return <span>No reports data available</span>;
    }

    return memoizedReports.map(report => (
      <div className="mb-1 w-100 col" key={report.id}>
        <div className="row wellness-report-header">
          <div className="col">
            <h1 className="text-left">{report.name}</h1>
          </div>
          <div className="ml-2 col-auto">
            <ReportDownloadSection textDownloadButton={true} url={downloadDnaPdf(report.id)} />
          </div>
        </div>
        <div>
          <p className="mb-1 small text-muted">
            <b>Date first generated: </b>
            {format_date(report.created)}
          </p>
          <p className="mb-1 small text-muted mb-3">
            <b>Date last updated: </b>
            {format_date(report.timestamp)}
          </p>
        </div>
        <div className="row">
          <div className="col-7 col-md">
            <h2 className="text-left">
              <b>Health areas</b>
            </h2>
          </div>
          {(report.goals?.length > 0 || report.exclusions?.length > 0) && (
            <div className="col-auto col-md-5">
              {report.goals?.length > 0 && (
                <div className="d-flex flex-row align-items-baseline flex-wrap">
                  <p className="m-0">Goals:</p>
                  {report.goals.map(renderGoalButton)}
                </div>
              )}
              {report.exclusions?.length > 0 && (
                <div className="d-flex flex-row align-items-baseline flex-wrap">
                  <p className="m-0">I don't eat/drink:</p>
                  {report.exclusions.map(exclusion => (
                    <span
                      key={exclusion.tag}
                      className="small-pill"
                    >
                      {fix_exclusions_name(exclusion.name)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="pt-3 pb-1">
          <ReportAccordion
            items={report.accordionItems}
            userName={userName}
          />
        </div>
        {isGlutenPartial && <DnaPartialReportCard partialMarker={partialGluten}/>}
      </div>
    ));
  };

  return (
    <div className="mb-4">
      <div className="container-fluid wellness-report-wrapper">
        <div className="row">
          <div className="col-md-12">
            <div className="dashboard">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessReportPage;